import styled from 'styled-components/macro';
import Helmet from 'react-helmet';
import React from 'react';
import { useTranslate } from '../contexts/languageContext';
import { useIsDesktop } from '../hooks/useMediaQuery';
import useIni from '../hooks/useIni';
import {
  responsive,
  nombreEleccion,
} from '../resources/constantes.json';
import { Link } from 'react-router-dom';

import qrAndroid from '../assets/img/qr-google-play.png';
import qriOS from '../assets/img/qr-app-store.png';
import logoAndroid from '../assets/img/logo-google-play.png';
import logoIOS from '../assets/img/logo-app-store.png';
import bgInicio from '../assets/img/bg-inicio.png';
import LogoRegistraduria from '../assets/img/logoRegisBlanco.png';
import LogoEleccion from '../assets/img/logoEleccionesIni.png';
import { useNomenclator } from '../contexts/nomenclatorContext';

/**
 * Página que sirve de inicio y que muestra un resumen de resultados para todas las elecciones
 */

const Inicio = ({ nomenclator }) => {
  const t = useTranslate();
  const isDesktop = useIsDesktop();
  const ini = useIni();
  const items = ini?.botones;
  const isClosed = ini?.isClosed;
  return (
    <LayoutInicio ini={ini}>
      {ini && (
        <Content>
          {isClosed ? (
            <TextContainer>
              <TextClosed>{t('inicioClose')}</TextClosed>
            </TextContainer>
          ) : (
            <LinkContainer desktop={isDesktop}>
              <ListaLinks items={items.filter(i => i.hab === 'activado')} />
            </LinkContainer>
          )}
        </Content>
      )}
    </LayoutInicio>
  );
};

export function LayoutInicio({ children, ini }) {
  const t = useTranslate();
  const isDesktop = useIsDesktop();
  const appsVisibles = ini?.appsVisibles;
  return (
    <>
      <Helmet>
        <title itemProp="name" lang="es">
          Presidente y Vicepresidente de la República 2022
        </title>
      </Helmet>
      <MainLayout>
        <MainContainer>
          <SLogoRegistraduriaTop>
            <img src={LogoRegistraduria} alt={'Logo Registraduria'} />
          </SLogoRegistraduriaTop>
          <SLogoEleccion>
            <img src={LogoEleccion} alt={'Logo elección'} />
          </SLogoEleccion>
          <TextWrapper>
            <MainTitle>
              <span>{t('tituloMainInicio1')}</span>
              <span>{t('tituloMainInicio2')}</span>
            </MainTitle>
            <Separator />
            <Notes>
						De conformidad con el parágrafo 2 del artículo 2 de la Resolución No. 1706 de 2019 del CNE, los boletines expedidos por la Registraduría Nacional del Estado Civil, 
						durante el denominado preconteo, no son de carácter vinculante y tienen mero carácter informativo, por lo que no pueden considerarse como documentos electorales que definan una elección. 
						<br/>Los porcentajes se calculan y muestran con reducción a 2 dígitos a la derecha del separador decimal (término matemático: truncamiento a 2 decimales).
						<br/>Es importante tener en cuenta que, el concepto de agrupación política utilizado incluye a los partidos y movimientos políticos con personería jurídica, grupos significativos de ciudadanos y coaliciones entre estos.
            </Notes>
          </TextWrapper>
          {children}
          <AppsContainer isDesktop={isDesktop}>
            <QrText>{t('qrText')}</QrText>
            <QrBlock isDesktop={isDesktop} visible={appsVisibles} />
          </AppsContainer>
          <SLogoRegistraduriaBottom>
            <img src={LogoRegistraduria} alt={'Logo Registraduria'} />
          </SLogoRegistraduriaBottom>
        </MainContainer>
      </MainLayout>
    </>
  );
}

const ListaLinks = ({ items }) => {
  const nomenclator = useNomenclator();
	const t = useTranslate();
  const richItems = items.filter(item => nomenclator.nomAmbitos[item.id] !== undefined).map(i => ({
    id: i.id,
    ...nomenclator.nomAmbitos[i.id],
  }));
  richItems.sort((a, b) => nomenclator.ordAmbitos.indexOf(a.id) - nomenclator.ordAmbitos.indexOf(b.id));
  return (
    <ListaUlWrapper>
      {richItems.map((item, index) => (
        <SLink key={item.id + '_' + index} to={nombreEleccion[item.id] + '/0'}>{item.n}</SLink>
      ))}
			<SLink 
			target="_blank"
			rel="noopener noreferrer" 
			to={{ pathname:"https://resultadosprecpresidente1v.registraduria.gov.co/presidente/0/colombia"}}>{t('resultadosPrimera')}</SLink>
    </ListaUlWrapper>
  );
};

export function QrBlock({ isDesktop, visible = true }) {
  const t = useTranslate();
  return (
    <>
      {visible && (
        <QrContainer>
          <AppBlock isDesktop={isDesktop}>
            {isDesktop && (
              <QRImage src={qriOS} alt={t('qriOS')} element="right" />
            )}
            <a rel="noopener noreferrer" target="_blank" href={'https://itunes.apple.com/app/1624770675'}>
              <ShopLogo src={logoIOS} alt="App Store" element="right" />
            </a>
          </AppBlock>
          <AppBlock isDesktop={isDesktop}>
            {isDesktop && (
              <QRImage src={qrAndroid} alt={t('qrAndroid')} element="left" />
            )}
            <a rel="noopener noreferrer" target="_blank" href={'https://play.google.com/store/apps/details?id=co.registraduria.gov.elecciones.presidencia.colombia2022'}>
              <ShopLogo src={logoAndroid} alt="Google Play" element="left" />
            </a>
          </AppBlock>
        </QrContainer>
      )}
    </>
  );
}

const MainLayout = styled.main`
  background-image: url(${bgInicio});
  background-size: cover;
  height: 100%;
  min-height: 100vh;
}
`;

const TextWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SLogoRegistraduriaTop = styled.div`
  position:fixed;
  top: 3%;
  left: 3%;
  min-height: 115px;
  @media screen and (max-width: ${responsive.tablet}px) {
    display:none;
  }
`;

const SLogoRegistraduriaBottom = styled.div`
  display:none;
  min-height: 115px;
  @media screen and (max-width: ${responsive.tablet}px) {
    display:block;
  }
`;

const TextContainer = styled.div`
  text-align: left;
  font-size: 2rem;
  margin-bottom: 14px;
  color: #2f435f;
  font-weight: 400;
`;
const Notes = styled.div`
  font-weight: 600;
  font-size: 1.3rem;
  box-sizing: border-box;
  padding: 0 0 0.5rem 0;
  color: #fff;
  width: 70%;
  text-align: center;
  @media screen and (max-width: ${responsive.mobile}px) {
    width: 90%;
  }
`;

/*
 *  Main layout
 */

const MainContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
`;

const SLogoEleccion = styled.div`
  width: 22%;
  min-width:200px;
  margin-top:6%;
	text-align:center;
  @media screen and (max-width: ${responsive.mobile}px) {
    width: 40%;
  }
	img{
		width: 80%;
		height: auto;
	}
`;

const MainTitle = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  line-height: initial;
  font-size: 28px;
  color: #ffffff;

  @media screen and (max-width: ${responsive.tablet}px) {
    font-size: 24px;
    width: 90%;  
  }
  
  @media screen and (max-width: ${responsive.mobile}px) {
    font-size: 20px;
  }

  span:first-child {
    font-weight: 100;
    @media screen and (max-width: ${responsive.tablet}px) {
      width: 90%;
      margin: 0 auto;
    }
  }
  span:nth-child(2) {
    font-weight: 500;
    @media screen and (max-width: ${responsive.tablet}px) {
      width: 90%;
      margin: 0 auto;
    }
  }
`;

const TextClosed = styled.p`
  font-family: Lato;
  font-weight: 400;
  font-size: 2rem;
  color: #01f0ea;
`;

const Separator = styled.span`
  border: 1px solid #01f0ea;
  height: 0;
  width: 75%;
  margin: 15px 0;
`;

const Content = styled.div``;

/*
 * Links
 */

const LinkContainer = styled.div`
  text-align: left;
  font-weight: 700;
  font-size: 1.8rem;
`;

const ListaUlWrapper = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 2rem 10rem;
  @media (max-width: ${responsive.tablet}px) {
    margin-top: 30px;
    display: block;
  }
`;

const SLink = styled(Link)`
  min-width:244px;
  padding: 17px 18px;
  font-size: 14px;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  color: #ffff;
  background-color: #04aa94;
  border-radius: 3px;
  @media (max-width: ${responsive.tablet}px) {
    margin-bottom: 5px;
  }
`;

/*
 * APPS
 */

const AppsContainer = styled.div`
  width: 50%;
  margin: max(2%, 20px) 0px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 85px;
  padding: 1rem 0;
  @media (max-width: ${responsive.tablet}px) {
    width: 70%;
  }
`;

const QrContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 0.5rem 3rem;
  @media (max-width: ${responsive.tablet}px) {
    justify-content: space-between;
  }
`;

const AppBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 105px;
  @media (max-width: ${responsive.tablet}px) {
    margin-right: 0px;
  }
`;

const QRImage = styled.img`
  height: auto;
  width: 100px;
`;

const ShopLogo = styled.img`
  height: 30px;
  margin-top: 0.5rem;
`;

const QrText = styled.span`
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  text-align: center;
  color: #172946;
`;

/**************************************************/
export default Inicio;
